import { v4 } from "uuid";
import { Artifact } from "../models/Artifact";
import axios from "axios";

export interface PaginatedArtifactsResult {
    items: Artifact[];
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
    hasMore: boolean;
}

export const getAllArtifacts = async (accessToken: string, options?: { page?: number; pageSize?: number; }): Promise<PaginatedArtifactsResult> => {
    const response = await axios.get(process.env.REACT_APP_API_URL + '/v1/artifacts', {
        headers: {
            'Authorization': accessToken,
            'X-Request-ID': v4()
        },
        params: {
            page: options?.page,
            pageSize: options?.pageSize
        }
    });

    if (response.status !== 200) {
        throw new Error('Failed to get artifacts');
    }

    const data = response.data as {
        items?: Artifact[];
        page?: number;
        pageSize?: number;
        total?: number;
        hasMore?: boolean;
    };

    const page = data.page ?? options?.page ?? 1;
    const pageSize = data.pageSize ?? options?.pageSize ?? 12;
    const total = data.total ?? 0;
    const totalPages = Math.max(1, Math.ceil(total / Math.max(pageSize, 1)));

    return {
        items: data.items ?? [],
        page,
        pageSize,
        total,
        totalPages,
        hasMore: data.hasMore ?? page < totalPages
    };
}

export const deleteArtifact = async (artifactId: string, accessToken: string): Promise<void> => {
    const response = await axios.delete(process.env.REACT_APP_API_URL + `/v1/artifacts/${artifactId}`, {
        headers: {
            'Authorization': accessToken,
            'X-Request-ID': v4()
        }
    });

    if (response.status !== 204) {
        throw new Error('Failed to delete artifact');
    }
}

export const createArtifact = async (artifact: {
    artifactType: string,
    data: string,
    hint?: string,
}, accessToken: string): Promise<string> => {
    const response = await axios.post(process.env.REACT_APP_API_URL + '/v1/artifacts', artifact, {
        headers: {
            'Authorization': accessToken,
            'X-Request-ID': v4()
        }
    });

    if (response.status !== 201) {
        throw new Error('Failed to create artifact');
    }

    return response.data as string;
}

export const updateArtifact = async (artifact: Artifact, accessToken: string): Promise<void> => {
    const response = await axios.put(process.env.REACT_APP_API_URL + `/v1/artifacts/${artifact.id}`, artifact, {
        headers: {
            'Authorization': accessToken,
            'X-Request-ID': v4()
        }
    });

    if (response.status !== 204) {
        throw new Error('Failed to update artifact');
    }
}
